import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { maintenanceAPI } from '../services/api';

const MaintenanceContext = createContext();

export const useMaintenanceStatus = () => useContext(MaintenanceContext);

const POLL_INTERVAL_MS = 30 * 1000; // 30 seconds

export const MaintenanceProvider = ({ children }) => {
  const [status, setStatus] = useState({
    maintenance_mode: false,
    schedule: null,
  });
  const [loading, setLoading] = useState(true);
  // Offset = server_time - client_time (in ms). Positive means server is ahead.
  const [serverOffsetMs, setServerOffsetMs] = useState(0);
  const intervalRef = useRef(null);

  const fetchStatus = useCallback(async () => {
    try {
      const beforeMs = Date.now();
      const res = await maintenanceAPI.getStatus();
      const afterMs = Date.now();

      if (res.data?.success) {
        setStatus(res.data.data);

        // Calculate server-client clock offset using round-trip midpoint
        if (res.data.data.server_time) {
          const serverMs = new Date(res.data.data.server_time).getTime();
          const clientMidpoint = (beforeMs + afterMs) / 2;
          setServerOffsetMs(serverMs - clientMidpoint);
        }
      }
    } catch {
      // Fail silently — don't block UI
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchStatus();
    intervalRef.current = setInterval(fetchStatus, POLL_INTERVAL_MS);
    return () => clearInterval(intervalRef.current);
  }, [fetchStatus]);

  // Computed helpers
  const isInMaintenance = status.maintenance_mode;
  const hasUpcomingMaintenance = !!(status.schedule && !status.schedule.in_maintenance_window);
  const schedule = status.schedule;

  /**
   * Get current time corrected for server-client clock drift.
   * Use this instead of new Date() for all maintenance timing calculations.
   */
  const getServerNow = useCallback(() => {
    return new Date(Date.now() + serverOffsetMs);
  }, [serverOffsetMs]);

  return (
    <MaintenanceContext.Provider value={{
      isInMaintenance,
      hasUpcomingMaintenance,
      schedule,
      loading,
      refresh: fetchStatus,
      getServerNow,
      serverOffsetMs,
    }}>
      {children}
    </MaintenanceContext.Provider>
  );
};
